import React, { MutableRefObject, useEffect, useRef } from 'react';
import Portal from '../Portal';
import styled, { css } from 'styled-components';
import { ReactComponent as CloseCross } from '../Modal/closeCross.svg';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  z-index: 1000;
  overflow: auto;
`;

const ModalWrap = styled.div`
  padding: 25px 20px 25px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
  min-height: 100%;
  background: rgba(9, 21, 38, 0.6);
`;

const ModalWindow = styled.div<{ withoutPadding?: boolean }>`
  background: #fff;
  border-radius: 32px;
  position: relative;
  overflow: hidden;
  ${({ withoutPadding }) =>
    !withoutPadding &&
    css`
      padding: 25px 20px 25px 30px;
    `};
`;

const CloseButton = styled.button`
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 50px;
  background: var(--black-10);
  color: var(--font-color);
  cursor: pointer;
  line-height: 0;

  &:hover,
  &:focus {
    outline: none;
  }

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }

  svg path,
  svg rect {
    fill: var(--font-color) !important;
  }
`;

type ModalV2Props = {
  onClose: () => void;
  modalRef?: MutableRefObject<HTMLDivElement | undefined>;
  withoutPadding?: boolean;
  disableCloseByClickByOverlay?: boolean;
  hideCloseButton?: boolean;
  children?: React.ReactNode;
};

const ModalV2: React.FC<ModalV2Props> = ({
  onClose,
  modalRef,
  withoutPadding,
  disableCloseByClickByOverlay,
  hideCloseButton,
  children,
}) => {
  const mouseTargets = useRef({ down: null as EventTarget | null, up: null as EventTarget | null });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (!disableCloseByClickByOverlay && e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.classList.add('noScrollBody');

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('noScrollBody');
    };
  }, [disableCloseByClickByOverlay, onClose]);

  const handleOverlayInteraction = (e: React.MouseEvent) => {
    if (disableCloseByClickByOverlay) return;

    if (e.type === 'mousedown') mouseTargets.current.down = e.target;
    else if (e.type === 'mouseup') mouseTargets.current.up = e.target;
    else if (e.currentTarget === mouseTargets.current.down && e.currentTarget === mouseTargets.current.up) {
      onClose();
    }
  };

  const setRef = (node: HTMLDivElement | null) => {
    if (modalRef) modalRef.current = node ?? undefined;
  };

  return (
    <Portal>
      <Overlay data-testid="modal-overlay">
        <ModalWrap
          onMouseDown={handleOverlayInteraction}
          onMouseUp={handleOverlayInteraction}
          onClick={handleOverlayInteraction}
        >
          <ModalWindow withoutPadding={withoutPadding} ref={setRef} data-testid="modal-window">
            {!hideCloseButton && (
              <CloseButton type="button" onClick={onClose} aria-label="Закрыть" data-testid="modal-close-button">
                <CloseCross />
              </CloseButton>
            )}
            {children}
          </ModalWindow>
        </ModalWrap>
      </Overlay>
    </Portal>
  );
};

export default ModalV2;